import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { 
  AssinaturaResponse, 
  UpgradeResponse, 
  DowngradeResponse, 
  CancelarAssinaturaResponse, 
  AssinaturaOperacaoResponse,
  SolicitarEnterpriseRequest,
  SolicitarEnterpriseResponse 
} from 'app/shared/models/ei-models';
import { BaseService } from '../../core';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AssinaturaService extends BaseService {
  private controllerEndpoint = environment.urlEdducaImobApi.concat('api/Assinatura/');

  constructor(private httpClient: HttpClient) {
    super();
  }

  /**
   * Obtém a assinatura atual da imobiliária do usuário logado
   */
  getAssinaturaAtual(loader = true): Observable<AssinaturaResponse> {
    return this.httpClient.get<any>(
      this.controllerEndpoint.concat('Atual'),
      { headers: this.getHeader(loader) }
    ).pipe(
      map(response => new AssinaturaResponse(response))
    );
  }

  /**
   * Realiza upgrade do plano (efeito imediato)
   * - Novas funcionalidades liberadas instantaneamente
   * - Calcula valor proporcional para próxima fatura
   * @param novoPlanoId ID do novo plano (deve ter preço maior)
   */
  realizarUpgrade(novoPlanoId: number, loader = true): Observable<UpgradeResponse> {
    return this.httpClient.post<UpgradeResponse>(
      this.controllerEndpoint.concat('Upgrade'),
      { planoId: novoPlanoId },
      { headers: this.getHeader(loader) }
    );
  }

  /**
   * Solicita downgrade do plano (efeito na próxima cobrança)
   * - Mantém funcionalidades atuais até a data de efetivação
   * - Valida quantidade de usuários vs limite do novo plano
   * @param novoPlanoId ID do novo plano (deve ter preço menor)
   */
  solicitarDowngrade(novoPlanoId: number, loader = true): Observable<DowngradeResponse> {
    return this.httpClient.post<DowngradeResponse>(
      this.controllerEndpoint.concat('Downgrade'),
      { planoId: novoPlanoId },
      { headers: this.getHeader(loader) }
    );
  }

  /**
   * Cancela a assinatura (acesso mantido até próxima cobrança)
   * - Não gera novas cobranças
   * - Após expiração, usuários serão desativados
   */
  cancelarAssinatura(loader = true): Observable<CancelarAssinaturaResponse> {
    return this.httpClient.post<CancelarAssinaturaResponse>(
      this.controllerEndpoint.concat('Cancelar'),
      {},
      { headers: this.getHeader(loader) }
    );
  }

  /**
   * Cancela um downgrade agendado
   * - Mantém o plano atual
   * - Cobrança normal na próxima renovação
   */
  cancelarDowngradeAgendado(loader = true): Observable<AssinaturaOperacaoResponse> {
    return this.httpClient.post<AssinaturaOperacaoResponse>(
      this.controllerEndpoint.concat('CancelarDowngrade'),
      {},
      { headers: this.getHeader(loader) }
    );
  }

  /**
   * Reativa uma assinatura cancelada (antes da data de encerramento)
   * - Só funciona se ainda estiver dentro do período de acesso
   */
  reativarAssinatura(loader = true): Observable<AssinaturaOperacaoResponse> {
    return this.httpClient.post<AssinaturaOperacaoResponse>(
      this.controllerEndpoint.concat('Reativar'),
      {},
      { headers: this.getHeader(loader) }
    );
  }

  /**
   * Solicita upgrade para o plano Enterprise (requer contato comercial)
   * - Envia notificação para equipe comercial
   * - Backend já possui dados da imobiliária
   * @param request Dados opcionais da solicitação (mensagem)
   */
  solicitarEnterprise(request: SolicitarEnterpriseRequest, loader = true): Observable<SolicitarEnterpriseResponse> {
    return this.httpClient.post<SolicitarEnterpriseResponse>(
      this.controllerEndpoint.concat('SolicitarEnterprise'),
      request,
      { headers: this.getHeader(loader) }
    );
  }
}
